import React, { useState, useEffect, useMemo, SetStateAction } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { Spinner } from "reactstrap";
import { RiSearchLine } from "react-icons/ri";
import BaseButton from "Components/Base/BaseButton";
import BaseInput from "Components/Base/BaseInput";
import TableContainer from "Components/Base/BaseTable";
import DeleteModal from "Components/Base/DeleteModal";
import BaseModal from "Components/Base/BaseModal";
import {
  checkStatusCodeSuccess,
  Loading,
  notFound,
  Search,
} from "Components/constants/Common";
import {
  InputPlaceHolder,
  SelectPlaceHolder,
  validationMessages,
} from "Components/constants/validation";
import {
  ActionKey,
  ActionLabel,
  DeleteLabel,
  editLabel,
  NewLabel,
  cancelLabel,
  submitLabel,
  updateLabel,
} from "Components/constants/Role";
import {
  addHSNLabel,
  Code,
  codeKey,
  editHSNLabel,
  HSNSACLabel,
  Type,
  typeKey,
} from "Components/constants/HsnSac";
import {
  createHSN,
  deleteHSN,
  listOfHSN,
  updateHSN,
  viewHSN,
} from "Api/HsnSac";
import { BaseSelect } from "Components/Base/BaseSelect";

interface HSNSACPayload {
  code: string;
  type: number | null | any;
  hsnId?: number | any;
}
const HsnSacPage = () => {
  document.title = HSNSACLabel;

  const [loader, setLoader] = useState<boolean>(false);
  const [submitLoader, setSubmitLoader] = useState<boolean>(false);
  const [HsnSac, setHsnSac] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [selectedHSNId, setSelectedHSNId] = useState<number | null>(null);
  const [btnLoader, setBtnLoader] = useState<boolean>(false);
  const [modalLoader, setModalLoader] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [HsnSearchData, setHsnSearchData] = useState<Array<any>>([]);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);
  const [columnName, setColumnName] = useState<string>("id");
  const [sortOrder, setSortOrder] = useState<string>("desc");
  const [typeList, setTypeList] = useState<Array<any>>([
    { label: "HSN", value: 1 },
    { label: "SAC", value: 2 },
  ]);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    formik.resetForm();
    setSelectedHSNId(null);
    setEditMode(false);
  };

  const fetchHsn = async () => {
    const payload = {
      pageSize,
      page: currentPage,
      sortKey: columnName,
      sortValue: sortOrder,
    };
    setLoader(true);
    listOfHSN(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setHsnSac(res?.data?.hsn_sac || []);
          setTotalRecords(res?.data?.totalItems || 0);
          setTotalPages(res?.data?.totalPages);
          setCurrentPage(res?.data?.currentPage);
          setTotalNumberOfRows(res?.data?.numberOfRows);
          toast.success(res?.message);
        } else {
          setHsnSac([]);
        }
      })
      .catch(() => {
        setHsnSac([]);
      })
      .finally(() => setLoader(false));
  };

  const fetchSearchHSNData = () => {
    const payload = {
      pageSize,
      page: currentPage,
      search: searchValue,
      sortKey: columnName,
      sortValue: sortOrder,
    };
    setLoader(true);
    listOfHSN(payload)
      .then((response) => {
        if (checkStatusCodeSuccess(response.statusCode)) {
          setHsnSearchData(response?.data?.hsn_sac || []);
          setTotalRecords(response?.data?.totalItems || 0);
          setTotalPages(response?.data?.totalPages);
          setCurrentPage(response?.data?.currentPage);
          setTotalNumberOfRows(response?.data?.numberOfRows);
          toast.success(response?.message);
        } else {
          setHsnSearchData([]);
        }
      })
      .catch((error) => {
        setHsnSearchData([]);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  useEffect(() => {
    if (searchValue) {
      fetchSearchHSNData();
    } else {
      fetchHsn();
    }
  }, [currentPage, pageSize, searchValue, sortOrder, columnName]);

  const formik = useFormik({
    initialValues: {
      code: "",
      type: 1,
      hsnId: null,
    },
    validationSchema: Yup.object({
      code: Yup.string().required(validationMessages.required(Code)),
      type: Yup.number()
        .required(validationMessages.required(Type))
        .positive()
        .integer(),
    }),
    onSubmit: async (values) => {
      setSubmitLoader(true);
      const typeString = values.type === 1 ? "HSN" : "SAC";
      const payload: HSNSACPayload = {
        code: values.code,
        type: typeString,
        hsnId: values.hsnId,
      };

      if (selectedHSNId) {
        updateHSN(payload)
          .then((res) => {
            if (checkStatusCodeSuccess(res?.statusCode)) {
              toast.success(res?.message);
              fetchHsn();
              closeModal();
            } else {
              toast.error(res?.message);
            }
          })
          .catch((error) => {
            toast.error(error?.response?.data?.message || error?.message);
          })
          .finally(() => setSubmitLoader(false));
      } else {
        createHSN(payload)
          .then((res) => {
            if (checkStatusCodeSuccess(res?.statusCode)) {
              toast.success(res?.message);
              fetchHsn();
              closeModal();
            } else {
              toast.error(res?.message);
            }
          })
          .catch((error) => {
            toast.error(error?.response?.data?.message || error?.message);
            setSubmitLoader(false);
          })
          .finally(() => setSubmitLoader(false));
      }
    },
  });

  const openDeleteModal = (id: number) => {
    setSelectedHSNId(id);
    setDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setDeleteModal(false);
    setSelectedHSNId(null);
  };

  const handleDeleteHsn = () => {
    if (selectedHSNId !== null) {
      setBtnLoader(true);
      deleteHSN(selectedHSNId)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            toast.success(res?.message);
            fetchHsn();
          } else {
            toast.error(res?.message);
          }
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message || error?.message);
        })
        .finally(() => {
          setBtnLoader(false);
          closeDeleteModal();
        });
    }
  };

  const viewHsnData = (id: number) => {
    openModal();
    setModalLoader(true);
    viewHSN(id)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          const mappedType =
            res?.data?.type === "HSN" ? 1 : res?.data?.type === "SAC" ? 2 : 1;
          formik.setValues({
            code: res?.data?.code,
            type: mappedType,
            hsnId: res?.data?.id || null,
          });
          setSelectedHSNId(id);
        } else {
          toast.error(res?.message);
        }
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || error?.message);
      })
      .finally(() => setModalLoader(false));
  };

  const openEditModal = async (id: number) => {
    setEditMode(true);
    viewHsnData(id);
    setModalLoader(true);
  };

  const handleSearchValueChange = (value: any) => {
    if (value !== searchValue) {
      setCurrentPage(1);
      setSearchValue(value);
    }
  };

  const handleFetchData = (page: any) => {
    setCurrentPage(page);
  };

  const handleFetchSorting = (
    page: any,
    id: SetStateAction<string>,
    order: SetStateAction<string>
  ) => {
    setCurrentPage(page);
    setColumnName(id);
    setSortOrder(order);
  };

  const hsnColumns = useMemo(
    () => [
      {
        header: Code,
        accessorKey: codeKey,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: Type,
        accessorKey: typeKey,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: ActionLabel,
        accessorKey: ActionKey,
        cell: (cell: any) => (
          <div className="d-flex justify-content-start enable-action flex-wrap gap-2 icon">
            <span className="tooltip-container">
              <i
                className="mdi mdi-pencil-outline fs-4 text-info cursor-pointer"
                onClick={() => openEditModal(cell?.row?.original?.id)}
              ></i>
              <span className="tooltip-text">{editLabel}</span>
            </span>

            <span className="tooltip-container">
              <i
                className="mdi mdi-delete-outline fs-4 text-danger cursor-pointer"
                onClick={() => openDeleteModal(cell?.row?.original?.id)}
              ></i>
              <span className="tooltip-text">{DeleteLabel}</span>
            </span>
          </div>
        ),
        enableColumnFilter: false,
        enableSorting: false,
      },
    ],
    []
  );

  return (
    <div className="page-content">
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4 d-flex gap-4">{HSNSACLabel}</span>
          <div className="d-flex align-items-center gap-2">
            <BaseButton className="btn btn-success" onClick={openModal}>
              <i className="mdi mdi-plus" /> {NewLabel}
            </BaseButton>
          </div>
        </div>

        <div className="card py-2 px-4 overflow-x-auto">
          {loader || HsnSac.length > 0 ? (
            <TableContainer
              customPageSize={pageSize}
              setCustomPageSize={setPageSize}
              isGlobalFilter={true}
              columns={hsnColumns}
              data={searchValue ? HsnSearchData : HsnSac || []}
              tableClass="table bg-white"
              manualPagination={true}
              manualFiltering={true}
              tableLoader={loader}
              hasManualPagination={true}
              SearchPlaceholder={Search}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              onSearch={handleSearchValueChange}
              totalNumberOfRows={totalNumberOfRows}
              fetchData={handleFetchData}
              fetchSortingData={handleFetchSorting}
            />
          ) : (
            <div className="py-4 text-center">
              <div>
                <span className="fs-1 text-success">
                  <RiSearchLine />
                </span>
              </div>
              <div className="mt-4">
                <h5>{notFound.dataNotFound}</h5>
                <p className="text-muted">{notFound.thereIsNoDataFound}</p>
              </div>
            </div>
          )}
        </div>

        <BaseModal
          open={isModalOpen}
          toggle={closeModal}
          title={editMode ? editHSNLabel : addHSNLabel}
          submit={formik.handleSubmit}
          closeLabel={cancelLabel}
          submitLabel={editMode ? updateLabel : submitLabel}
          closeLabelColor="danger"
          submitLabelColor="success"
          loader={submitLoader}
        >
          {modalLoader ? (
            <div className="d-flex justify-content-center align-items-center">
              <Spinner>{Loading}</Spinner>
            </div>
          ) : (
            <div className="form-group">
              <BaseInput
                type="text"
                label={Code}
                placeholder={InputPlaceHolder(Code)}
                name="code"
                className="form-control"
                value={formik.values.code}
                onChange={formik.handleChange}
                handleBlur={formik.handleBlur}
                touched={formik.touched.code}
                error={formik.errors.code}
                required
              />

              <div className="mt-2">
                <BaseSelect
                  name="type"
                  label={Type}
                  options={typeList}
                  value={formik.values.type}
                  placeholder={SelectPlaceHolder(Type)}
                  handleChange={formik.setFieldValue}
                  handleBlur={formik.handleBlur}
                  error={formik.errors.type}
                  touched={formik.touched.type}
                  required
                />
              </div>
            </div>
          )}
        </BaseModal>

        <DeleteModal
          show={deleteModal}
          onCloseClick={closeDeleteModal}
          onDeleteClick={handleDeleteHsn}
          btnLoader={btnLoader}
        />
      </div>
    </div>
  );
};

export default HsnSacPage;
